import React from "react";
import { useLocation } from "react-router-dom";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "@/Components/Common/Tailwind";
import { useAuthContext } from "./AuthContext";
import { getUserOrdersByDate } from "@/api/services/package.service";
import { SUBDOMAIN } from "@/assets/meta/meta";
import {
  buildSameDayReservationRulesDismissKey,
  formatLocalDate,
  getOrderDoneResults,
  getSameDayReservationRules,
} from "@/pages/Order/orderDoneDisplay.mjs";
import { CalendarCheckIcon, InfoIcon, TriangleAlertIcon } from "lucide-react";

export const RESERVATION_RULES_STORAGE_VALUE = "agreed";

function hasReachedScrollEnd(element) {
  if (!element) return false;
  return element.scrollTop + element.clientHeight >= element.scrollHeight - 2;
}

function useScrollAgreement(rules) {
  const scrollRef = React.useRef(null);
  const [canConfirm, setCanConfirm] = React.useState(false);

  const measure = React.useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    setCanConfirm(hasReachedScrollEnd(element));
  }, []);

  React.useEffect(() => {
    setCanConfirm(false);
    const timeoutId = window.setTimeout(measure, 0);
    window.addEventListener("resize", measure);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener("resize", measure);
    };
  }, [measure, rules]);

  return {
    scrollRef,
    canConfirm,
    onScroll: measure,
  };
}

const SECTION_ICONS = {
  이용안내: InfoIcon,
  유의사항: TriangleAlertIcon,
};

function RuleSections({ rules }) {
  return (
    <div className="space-y-4">
      {rules.map((rule, index) => (
        <section
          key={`${rule.id ?? index}-${rule.reservationDate}`}
          className="overflow-hidden rounded-2xl bg-white shadow-[0_2px_20px_rgba(0,0,0,0.04)]"
        >
          {/* 상품 정보 요약 (탈 박스) */}
          <div className="flex flex-col gap-2 px-4 pt-4">
            <p className="m-0 truncate text-lg font-bold text-gray-900" style={{ fontFamily: "Gmarket-B" }}>
              {rule.name}
            </p>
            {rule.reservationDate ? (
              <p className="m-0 mt-1.5 flex items-center gap-1.5 text-sm text-gray-900">
                <CalendarCheckIcon size={16} className="shrink-0" />
                {String(rule.reservationDate).replace(/-/g, ".")} 이용 예정
              </p>
            ) : null}
            <div className="mt-3 border-b border-gray-100" />
          </div>

          {/* 이용안내 / 유의사항 */}
          <div className="space-y-5 p-4 pt-3">
            {rule.sections.map((section) => {
              const SectionIcon = SECTION_ICONS[section.title] || InfoIcon;
              return (
              <div key={section.key}>
                <div className="mb-2 flex flex-col gap-2">
                <h3 className="mb-2 flex items-center gap-1.5 text-sm font-bold text-gray-900">
                  <SectionIcon size={16} className="shrink-0 text-[#e16036]" aria-hidden="true" />
                  {section.title}
                </h3>
                </div>
                <div
                  className="text-sm leading-[1.8] text-gray-700 [&_img]:max-w-full [&_li]:mb-1.5 [&_p]:mb-2.5"
                  dangerouslySetInnerHTML={{ __html: section.html }}
                />
              </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

export function ReservationRulesGate({ rules, onConfirm }) {
  const { scrollRef, canConfirm, onScroll } = useScrollAgreement(rules);

  if (!rules.length) return null;

  return (
    <div className="flex h-dvh flex-col bg-gray-100">
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="min-h-0 flex-1 overflow-y-auto"
      >
        <div className="sticky top-0 z-10 flex h-18 items-center justify-center border-b border-gray-200/60 bg-white/50 px-5 backdrop-blur-md">
          <h1 className="m-0 text-xl font-bold text-gray-900" style={{ fontFamily: "Gmarket-B" }}>이용 수칙 안내</h1>
        </div>

        <div className="px-4 py-4">
          <h2 className="m-0 mb-4 px-1 text-lg font-semibold text-gray-900" style={{ fontFamily: "Gmarket-B" }}>
            즐거운 이용을 위해 확인해 주세요.
          </h2>
          <RuleSections rules={rules} />
        </div>
      </div>

      <div className="shrink-0 border-t border-gray-200/60 bg-white/50 px-4 py-3 backdrop-blur-md">
        <button
          type="button"
          onClick={onConfirm}
          disabled={!canConfirm}
          className={[
            "w-full rounded-xl px-4 py-3.5 text-base font-bold transition-colors",
            canConfirm
              ? "bg-[#e16036] text-white shadow-[0_4px_14px_rgba(225,96,54,0.25)] hover:bg-[#cc5530]"
              : "cursor-not-allowed bg-gray-200 text-gray-400",
          ].join(" ")}
        >
          {canConfirm ? "수칙 동의 및 예약 완료" : "안내를 끝까지 확인해 주세요"}
        </button>
      </div>
    </div>
  );
}

export default function TodayReservationRulesNotice() {
  const { accessToken, userInfo, isLoading } = useAuthContext();
  const location = useLocation();
  const [rules, setRules] = React.useState([]);
  const [dismissKey, setDismissKey] = React.useState("");
  const [isOpen, setIsOpen] = React.useState(false);
  const { scrollRef, canConfirm, onScroll } = useScrollAgreement(rules);

  React.useEffect(() => {
    if (isLoading || !accessToken || !userInfo?.id || location.pathname.startsWith("/order-done")) {
      setIsOpen(false);
      return;
    }

    let ignore = false;
    const today = formatLocalDate();

    const fetchTodayRules = async () => {
      try {
        const response = await getUserOrdersByDate(today, SUBDOMAIN);
        if (ignore) return;

        const orders = getOrderDoneResults(response.data);
        const nextRules = getSameDayReservationRules(orders, today);
        const nextDismissKey = buildSameDayReservationRulesDismissKey(userInfo.id, orders, today);
        setRules(nextRules);
        setDismissKey(nextDismissKey);
        setIsOpen(nextRules.length > 0 && localStorage.getItem(nextDismissKey) !== RESERVATION_RULES_STORAGE_VALUE);
      } catch (error) {
        console.error(error);
        if (!ignore) {
          setRules([]);
          setDismissKey("");
          setIsOpen(false);
        }
      }
    };

    fetchTodayRules();

    return () => {
      ignore = true;
    };
  }, [accessToken, isLoading, location.pathname, userInfo?.id]);

  const handleAgree = () => {
    if (dismissKey) {
      localStorage.setItem(dismissKey, RESERVATION_RULES_STORAGE_VALUE);
    }
    setIsOpen(false);
  };

  if (!rules.length) return null;

  return (
    <Modal isOpen={isOpen} title="이용수칙 안내" variant="sheet" className="gap-0">
      <ModalHeader>이용수칙 안내</ModalHeader>
      <ModalBody className="bg-gray-50">
        <div ref={scrollRef} onScroll={onScroll} className="max-h-[58vh] overflow-y-auto pr-1">
          <RuleSections rules={rules} />
        </div>
      </ModalBody>
      <ModalFooter className="rounded-none bg-white">
        {canConfirm ? (
          <Button type="button" variant="primary" onClick={handleAgree} className="w-full">
            동의하고 닫기
          </Button>
        ) : (
          <div className="flex min-h-10 w-full items-center justify-center text-sm text-gray-500">
            안내를 끝까지 확인해 주세요.
          </div>
        )}
      </ModalFooter>
    </Modal>
  );
}
